/**
 * SessionExpiryListener — global handler for mid-session 401s.
 *
 * Phase 1 Step 1c MUST (Push #90 pre-E2E sweep). Listens for the
 * `olytic:session-expired` event fired from `ApiError`'s constructor
 * whenever ANY API call returns 401 mid-flow. When the user was
 * believed authenticated, surfaces a friendly toast and redirects to
 * `/sign-in?next=<currentPath>&email=<currentEmail>` so they don't
 * have to retype the address they just lost the session for.
 *
 * Guards (in order):
 *   1. Only fires when session status was `authenticated` — otherwise
 *      the initial /api/me 401 on cold-start would double-toast.
 *   2. Skips when the current path is itself an auth surface
 *      (/sign-in / /sign-up / /accept-invite / /forgot-password /
 *      /reset-password / /verify-email / /welcome) — those flows
 *      manage their own 401s.
 *   3. Module-level debounce timestamp ensures duplicate fast events
 *      (when multiple in-flight requests all 401 at once) don't
 *      stack multiple navigations.
 *
 * Renders nothing — listener-only.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import { useToast } from './Toast.tsx';

// Module-level debounce so simultaneous 401s (e.g. parallel fetches in
// the same dispatch cycle) don't navigate twice. 5-second window is
// plenty wide for a typical burst + narrow enough that a real new
// session-expiry event later in the same tab still fires correctly.
let lastHandledAt = 0;

// Allowlist of paths the listener treats as "already an auth flow" and
// skips. Keeps the redirect from looping when the SPA is mid-sign-in
// and a stale fetch happens to 401.
const AUTH_PATH_PREFIXES: readonly string[] = [
  '/sign-in',
  '/sign-up',
  '/accept-invite',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/welcome',
  '/oauth/',
];

function isAuthPath(path: string): boolean {
  return AUTH_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function SessionExpiryListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, status } = useSession();
  const { showToast } = useToast();

  useEffect(() => {
    function handleSessionExpired() {
      // Guard 1: only react if we believed we WERE authenticated.
      // Cold-start /api/me 401 ⇒ status='loading' → 'unauthenticated',
      // never hits 'authenticated' — so we don't double-toast.
      if (status !== 'authenticated') return;

      // Guard 2: skip if current path is itself an auth flow.
      const currentPath = location.pathname + location.search;
      if (isAuthPath(location.pathname)) return;

      // Guard 3: debounce — collapse fast-fire bursts to one navigate.
      const now = Date.now();
      if (now - lastHandledAt < 5000) return;
      lastHandledAt = now;

      // Build the destination: /sign-in?next=<path>&email=<email>.
      // Encode both so query parsing on the SignIn page is reliable.
      const params = new URLSearchParams();
      // Skip `next` when current path is /home (default landing) —
      // sign-in already routes there post-auth, no need to over-specify.
      if (currentPath !== '/home' && currentPath !== '/') {
        // Sub-agent HHHH Track 3: multi-step wizards persist their state
        // in sessionStorage against the mid-flow 401. Append `resume=1`
        // to the wizard's `next` URL so it can surface a "picked up where
        // you left off" toast on re-entry. Path-based allowlist — cheap
        // to extend as future wizards land.
        const RESUMABLE_WIZARD_PREFIXES = ['/admin/connectors/byo'] as const;
        const isResumable = RESUMABLE_WIZARD_PREFIXES.some((p) =>
          location.pathname === p || location.pathname.startsWith(`${p}?`),
        );
        if (isResumable) {
          // Merge resume=1 into the existing query string on the return
          // path so a wizard that already carries `?step=3` (or similar)
          // keeps its params.
          const [pathOnly, existingQs] = currentPath.split('?', 2);
          const merged = new URLSearchParams(existingQs ?? '');
          merged.set('resume', '1');
          params.set('next', `${pathOnly}?${merged.toString()}`);
        } else {
          params.set('next', currentPath);
        }
      }
      if (user?.email !== undefined && user.email !== '') {
        params.set('email', user.email);
      }
      const qs = params.toString();
      const target = qs !== '' ? `/sign-in?${qs}` : '/sign-in';

      showToast({
        kind: 'info',
        message: 'Your session expired. Please sign in again to continue.',
      });

      navigate(target, { replace: true });
    }

    window.addEventListener('olytic:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('olytic:session-expired', handleSessionExpired);
    };
  }, [navigate, location, user, status, showToast]);

  return null;
}
